import React, { useEffect } from 'react';
import cx from 'classnames';
import {
  ToastContent,
  ToastOptions,
  TypeOptions,
  ToastContentProps,
} from 'react-toastify';

import {
  DEFAULT_TOAST_DURATION,
  BASE_ALERTS_CONTAINER_ID,
} from 'utils/toastUtils';
import BaseToast from 'components/wrappers/BaseToast';

import styles from './BaseAlert.module.less';

type OwnProps = {
  type: TypeOptions;
  message: ToastContent;
};

// ToastContentProps are embeded by toast when using custom component
type Props = OwnProps & Partial<ToastContentProps> & ToastOptions;

export enum TYPE {
  INFO = 'info',
  SUCCESS = 'success',
  WARNING = 'warning',
  ERROR = 'error',
  DEFAULT = 'default',
}

const ALERT_DEFAULT_OPTIONS: ToastOptions = {
  containerId: BASE_ALERTS_CONTAINER_ID,
};

const getAlertIcon = (type?: TypeOptions) => {
  switch (type) {
    case TYPE.WARNING:
      return 'icon-attention-filled';

    case TYPE.ERROR:
      return 'icon-closed-filled';

    case TYPE.SUCCESS:
      return 'icon-check-filled';

    case TYPE.INFO:
      return 'icon-info-filed';

    case TYPE.DEFAULT:
    default:
      return '';
  }
};

const getAlertTitle = (type?: TypeOptions) => {
  switch (type) {
    case TYPE.WARNING:
      return 'Please review.';

    case TYPE.ERROR:
      return 'Something went wrong.';

    case TYPE.SUCCESS:
      return 'Success!';

    case TYPE.INFO:
      return 'Info!';

    case TYPE.DEFAULT:
    default:
      return '';
  }
};

function AlertComponent(props: Props) {
  const { type, closeToast, message, closeButton = true, toastProps } = props;
  const alertIcon = getAlertIcon(type);
  const alertTitle = getAlertTitle(type);

  useEffect(() => {
    return () => {
      // Remove toastId from container
      BaseToast.dismiss(toastProps?.toastId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={styles.alertMessage}>
      {alertIcon && (
        <span className={cx('icon', alertIcon, styles.alertIcon)} />
      )}

      <div className={styles.alertBox}>
        {alertTitle && <div className={styles.alertTitle}>{alertTitle}</div>}
        <div className={styles.alertText}>{message}</div>
      </div>

      {closeButton && (
        <div className={styles.alertClose}>
          <span className="icon icon-close-sm" onClick={closeToast} />
        </div>
      )}
    </div>
  );
}

// Extend custom implementation with toast features
const BaseAlert = (message: ToastContent, options: ToastOptions) => {
  const alertOptions = { ...ALERT_DEFAULT_OPTIONS, ...options };

  return BaseToast(
    <AlertComponent {...alertOptions} type={TYPE.DEFAULT} message={message} />,
    alertOptions,
  );
};

BaseAlert.success = (message: ToastContent, options: ToastOptions) => {
  const autoClose = options?.autoClose || DEFAULT_TOAST_DURATION;
  const alertOptions = { ...ALERT_DEFAULT_OPTIONS, ...options };

  return BaseToast.success(
    <AlertComponent
      {...alertOptions}
      autoClose={autoClose}
      type={TYPE.SUCCESS}
      message={message}
    />,
    { ...alertOptions, autoClose },
  );
};

BaseAlert.info = (message: ToastContent, options: ToastOptions) => {
  const alertOptions = { ...ALERT_DEFAULT_OPTIONS, ...options };

  return BaseToast.info(
    <AlertComponent {...options} type={TYPE.INFO} message={message} />,
    alertOptions,
  );
};

BaseAlert.error = (message: ToastContent, options: ToastOptions) => {
  const alertOptions = { ...ALERT_DEFAULT_OPTIONS, ...options };

  return BaseToast.error(
    <AlertComponent {...alertOptions} type={TYPE.ERROR} message={message} />,
    alertOptions,
  );
};

BaseAlert.warning = (message: ToastContent, options: ToastOptions) => {
  const alertOptions = { ...ALERT_DEFAULT_OPTIONS, ...options };

  return BaseToast.warning(
    <AlertComponent {...alertOptions} type={TYPE.WARNING} message={message} />,
    alertOptions,
  );
};

BaseAlert.warn = BaseAlert.warning;
BaseAlert.dismiss = BaseToast.dismiss;
BaseAlert.clearWaitingQueue = BaseToast.clearWaitingQueue;
BaseAlert.isActive = BaseToast.isActive;
BaseAlert.update = BaseToast.update;
BaseAlert.done = BaseToast.done;
BaseAlert.onChange = BaseToast.onChange;
BaseAlert.POSITION = BaseToast.POSITION;
BaseAlert.TYPE = BaseToast.TYPE;

export default BaseAlert as typeof BaseToast;
